import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import useAuth from './useAuth'
import { redirectToOnboarding, clearOnboardingRedirect } from './onboarding'
import { getOnboardingStatus } from '../redux/actions/auth'

import LoaderComponent from '../components/CommonComponents/LoaderComponent'

// The OIDC callback and sign-out must work before the user is ready.
const UNGATED_PATHS = ['/login', '/auth', '/logout']
const RETRY_DELAYS_MS = [1000, 2000, 4000]

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const GateMessage = ({ text, buttonLabel, onClick }) => (
  <div className="auth-layout">
    <div className="auth-page">
      <div className="auth-form">
        <p>{ text }</p>
        { buttonLabel && (
          <button type="button" className="btn btn-navy" onClick={onClick}>
            { buttonLabel }
          </button>
        ) }
      </div>
    </div>
  </div>
)

// A Cognito login doesn't mean the user has a UMS profile yet: users who haven't
// finished Hub onboarding are sent there first (platform migration guide, section 5).
const OnboardingGate = ({ children }) => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { isLoading, isAuthenticated, getAccessTokenSilently, logout } = useAuth()
  const [verdict, setVerdict] = useState('CHECKING')
  const [attempt, setAttempt] = useState(0)

  const isGated = !isLoading && isAuthenticated && !UNGATED_PATHS.includes(pathname)

  useEffect(() => {
    if (!isGated || verdict === 'READY') {
      return undefined
    }

    let cancelled = false

    const fetchStatus = async () => {
      try {
        const accessToken = await getAccessTokenSilently()
        return await dispatch(getOnboardingStatus(accessToken))
      } catch (error) {
        return 'UNAVAILABLE'
      }
    }

    const check = async () => {
      setVerdict('CHECKING')
      let status = await fetchStatus()
      for (const delay of RETRY_DELAYS_MS) {
        if (cancelled || status !== 'UNAVAILABLE') {
          break
        }
        await sleep(delay)
        if (!cancelled) {
          status = await fetchStatus()
        }
      }
      if (cancelled) {
        return
      }

      if (status === 'READY') {
        clearOnboardingRedirect()
        setVerdict('READY')
      } else if (status === 'ONBOARDING_REQUIRED') {
        setVerdict(redirectToOnboarding() ? 'REDIRECTING' : 'ONBOARDING_STUCK')
      } else if (status === 'UNAUTHORIZED') {
        logout()
      } else {
        setVerdict(status === 'UNKNOWN' ? 'UNKNOWN' : 'UNAVAILABLE')
      }
    }

    check()

    return () => {
      cancelled = true
    }
  }, [isGated, attempt]) // eslint-disable-line

  if (!isGated || verdict === 'READY') {
    return children
  }

  if (verdict === 'ONBOARDING_STUCK') {
    return (
      <GateMessage
        text='Please finish setting up your Carbon6 profile to continue to PPC Entourage.'
        buttonLabel='Complete your Carbon6 profile'
        onClick={() => redirectToOnboarding({ force: true })}
      />
    )
  }

  if (verdict === 'UNKNOWN') {
    return (
      <GateMessage text="We couldn't confirm your Carbon6 account. Please contact support." />
    )
  }

  if (verdict === 'UNAVAILABLE') {
    return (
      <GateMessage
        text="We couldn't reach the Carbon6 account service."
        buttonLabel='Try again'
        onClick={() => setAttempt(value => value + 1)}
      />
    )
  }

  return <LoaderComponent />
}

export default OnboardingGate
